import { Button, Input, Label } from "@safeplate/ui";
import { Apple, ArrowRight, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";

import {
  requestPasswordReset,
  signInWithEmail,
  signInWithSocialProvider,
  signUpWithEmail,
} from "../api/auth.js";
import { displayError } from "../api/errors.js";
import { Brand } from "../components/brand.js";
import { useDocumentTitle } from "../components/document-title.js";
import { SlowStartNotice } from "../components/slow-start.js";

export type AuthMode = "sign-in" | "sign-up";

function AuthFrame({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-dvh items-center justify-center bg-canvas px-4 py-10 sm:px-10">
      <div className="w-full max-w-md">
        <div className="mb-10">
          <Brand />
        </div>
        {children}
      </div>
    </main>
  );
}

function SocialButton({
  disabled,
  onClick,
  provider,
}: {
  disabled: boolean;
  onClick: () => void;
  provider: "apple" | "google";
}) {
  return (
    <Button
      aria-label={`Continue with ${provider === "apple" ? "Apple" : "Google"}`}
      className="w-full bg-surface"
      disabled={disabled}
      onClick={onClick}
      variant="outline"
    >
      {provider === "apple" ? (
        <Apple className="size-4" aria-hidden="true" />
      ) : (
        <span
          className="grid size-4 place-items-center text-sm font-black text-blue-700"
          aria-hidden="true"
        >
          G
        </span>
      )}
      {provider === "apple" ? "Apple" : "Google"}
    </Button>
  );
}

export function AuthScreen({
  beforeSocialRedirect,
  context = "default",
  mode,
  onAuthenticated,
  onSwitchMode,
}: {
  beforeSocialRedirect?: () => void;
  context?: "default" | "invitation";
  mode: AuthMode;
  onAuthenticated: () => Promise<void>;
  onSwitchMode?: () => void;
}) {
  const creatingAccount = mode === "sign-up";
  const joiningTeam = context === "invitation";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  useDocumentTitle(
    verificationSent
      ? "Check your email"
      : creatingAccount
        ? joiningTeam
          ? "Create your account"
          : "Set up your business"
        : "Sign in",
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setPending(true);
    try {
      if (creatingAccount) {
        await signUpWithEmail({
          email: email.trim(),
          name: name.trim(),
          password,
        });
        setVerificationSent(true);
      } else {
        await signInWithEmail({ email: email.trim(), password });
        await onAuthenticated();
      }
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  async function social(provider: "apple" | "google") {
    setError(null);
    setPending(true);
    try {
      beforeSocialRedirect?.();
      const redirectUrl = await signInWithSocialProvider(provider);
      if (redirectUrl) {
        globalThis.location.assign(redirectUrl);
      } else {
        await onAuthenticated();
      }
    } catch (caught) {
      setError(displayError(caught));
      setPending(false);
    }
  }

  if (verificationSent) {
    return (
      <AuthFrame>
        <div className="rounded-md border border-stone-200 bg-surface p-6 sm:p-8">
          <CheckCircle2
            className="size-6 text-emerald-800"
            aria-hidden="true"
          />
          <h1 className="mt-4 text-3xl font-semibold tracking-tight text-stone-950">
            Check your email
          </h1>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            We sent a link to <strong>{email}</strong>.
          </p>
          {onSwitchMode ? (
            <button
              className="mt-7 inline-flex text-sm font-bold text-emerald-800 hover:text-emerald-950"
              onClick={onSwitchMode}
              type="button"
            >
              Back to sign in
            </button>
          ) : (
            <Link
              className="mt-7 inline-flex text-sm font-bold text-emerald-800 hover:text-emerald-950"
              to="/sign-in"
            >
              Back to sign in
            </Link>
          )}
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame>
      <h1 className="mb-8 text-3xl font-semibold tracking-tight text-stone-950 sm:text-4xl">
        {creatingAccount
          ? joiningTeam
            ? "Create your account"
            : "Set up your business"
          : "Sign in"}
      </h1>
      <div className="grid grid-cols-2 gap-3">
        <SocialButton
          disabled={pending}
          onClick={() => void social("google")}
          provider="google"
        />
        <SocialButton
          disabled={pending}
          onClick={() => void social("apple")}
          provider="apple"
        />
      </div>
      <div className="my-6 flex items-center gap-4 text-[0.7rem] font-bold tracking-widest text-stone-400 uppercase">
        <span className="h-px flex-1 bg-stone-300" />
        or
        <span className="h-px flex-1 bg-stone-300" />
      </div>
      <form className="space-y-4" onSubmit={(event) => void submit(event)}>
        {creatingAccount ? (
          <div className="space-y-2">
            <Label htmlFor="name">Your name</Label>
            <Input
              autoComplete="name"
              id="name"
              onChange={(event) => setName(event.target.value)}
              required
              value={name}
            />
          </div>
        ) : null}
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            autoComplete="email"
            id="email"
            onChange={(event) => setEmail(event.target.value)}
            required
            type="email"
            value={email}
          />
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-3">
            <Label htmlFor="password">
              {creatingAccount ? "Password (10+ characters)" : "Password"}
            </Label>
            {!creatingAccount ? (
              <Link
                className="text-xs font-bold text-emerald-800 hover:text-emerald-950"
                to="/forgot-password"
              >
                Forgot password?
              </Link>
            ) : null}
          </div>
          <div className="relative">
            <Input
              autoComplete={
                creatingAccount ? "new-password" : "current-password"
              }
              className="pr-12"
              id="password"
              minLength={10}
              onChange={(event) => setPassword(event.target.value)}
              required
              type={passwordVisible ? "text" : "password"}
              value={password}
            />
            <button
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-0 grid w-11 place-items-center text-stone-400 hover:text-stone-700"
              onClick={() => setPasswordVisible((visible) => !visible)}
              type="button"
            >
              {passwordVisible ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        </div>
        {error ? (
          <p
            className="rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-800"
            role="alert"
          >
            {error}
          </p>
        ) : null}
        <Button className="mt-2 w-full" disabled={pending} type="submit">
          {pending
            ? "Please wait…"
            : creatingAccount
              ? "Create account"
              : "Sign in"}
          {!pending ? (
            <ArrowRight className="size-4" aria-hidden="true" />
          ) : null}
        </Button>
        <div className="flex justify-center">
          <SlowStartNotice active={pending} />
        </div>
      </form>
      <p className="mt-7 text-center text-sm text-stone-600">
        {creatingAccount ? "Already have an account?" : "New to SafePlate?"}{" "}
        {onSwitchMode ? (
          <button
            className="font-bold text-emerald-800 hover:text-emerald-950"
            onClick={onSwitchMode}
            type="button"
          >
            {creatingAccount ? "Sign in" : "Create one"}
          </button>
        ) : (
          <Link
            className="font-bold text-emerald-800 hover:text-emerald-950"
            to={creatingAccount ? "/sign-in" : "/sign-up"}
          >
            {creatingAccount ? "Sign in" : "Create one"}
          </Link>
        )}
      </p>
    </AuthFrame>
  );
}

export function ForgotPasswordScreen() {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [sent, setSent] = useState(false);
  useDocumentTitle(sent ? "Check your email" : "Reset your password");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (caught) {
      setError(displayError(caught));
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthFrame>
      <h1 className="text-3xl font-semibold tracking-tight text-stone-950 sm:text-4xl">
        {sent ? "Check your email" : "Reset your password"}
      </h1>
      {sent ? (
        <p className="mt-3 text-sm leading-6 text-stone-600">
          If that email has an account, a reset link is on its way.
        </p>
      ) : null}
      {sent ? (
        <Link
          className="mt-7 inline-flex text-sm font-bold text-emerald-800"
          to="/sign-in"
        >
          Back to sign in
        </Link>
      ) : (
        <form
          className="mt-7 space-y-4"
          onSubmit={(event) => void submit(event)}
        >
          <div className="space-y-2">
            <Label htmlFor="reset-email">Email</Label>
            <Input
              autoComplete="email"
              id="reset-email"
              onChange={(event) => setEmail(event.target.value)}
              required
              type="email"
              value={email}
            />
          </div>
          {error ? (
            <p className="text-sm text-red-800" role="alert">
              {error}
            </p>
          ) : null}
          <Button className="w-full" disabled={pending} type="submit">
            {pending ? "Sending…" : "Send reset link"}
          </Button>
          <div className="flex justify-center">
            <SlowStartNotice active={pending} />
          </div>
        </form>
      )}
    </AuthFrame>
  );
}
